/**
 * 12-Week Grunt Program
 * Intermediate tactical fitness with upper/lower split
 * 4 days per week: Lower, Upper, Conditioning, Endurance
 */

import type { WorkoutProgram, WorkoutTemplateDay, ProgramMetadata, ProgramPeriodizationModel } from '../types/program';

const metadata: ProgramMetadata = {
  id: 'grunt-12week',
  name: '12-Week Grunt Development',
  description: 'Intermediate tactical fitness with structured upper/lower split',
  level: 'grunt',
  duration: 12,
  daysPerWeek: 4,
  goals: ['tactical-performance', 'strength', 'endurance', 'work-capacity'],
  difficulty: 'intermediate',
  version: '1.0.0',
  createdAt: new Date(),
  updatedAt: new Date(),
  isActive: true,
  hasRangeDay: false, // 4-day program; D5-D7 are rest — no range/enhancement content
};

// Grunt periodization (moderate progression)
const periodization: ProgramPeriodizationModel = {
  style: 'linear',
  phases: [
    // Week 1 & 12: Testing
    {
      weeks: [1, 12],
      type: 'assessment',
      name: 'Fitness Testing',
      focus: 'Establish baseline and demonstrate progress',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0
    },
    // Weeks 2-6: Accumulation
    {
      weeks: [2, 3, 4, 5, 6],
      type: 'accumulation',
      name: 'Volume Building',
      focus: 'Building work capacity and muscle endurance',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0,
      progression: {
        type: 'linear',
        startIntensity: 0.65,
        endIntensity: 0.75,
        weeklyIncrease: 0.02
      },
      trainingParameters: {
        repRange: [6, 10],
        setsMultiplier: 1.0,
        restPeriod: '90-120s'
      }
    },
    // Week 7: Deload — ADDED. The program previously ran 10 straight weeks of
    // progressive overload (accumulation W2-6 → intensification W7-11) with no
    // recovery week at all, then tested maxes at W12. An intermediate arrives
    // at that retest fatigued and posts a number below their true peak. Halving
    // volume and dropping intensity here lets accumulated fatigue clear so the
    // intensification block starts fresh — same rationale as Specialist's W6.
    {
      weeks: [7],
      type: 'deload',
      name: 'Recovery Deload',
      focus: 'Clear fatigue before the strength block',
      intensityMultiplier: 0.85,
      volumeMultiplier: 0.5,
      progression: {
        type: 'linear',
        startIntensity: 0.65,
        endIntensity: 0.65,
        weeklyIncrease: 0
      },
      trainingParameters: {
        repRange: [6, 8],
        setsMultiplier: 0.5,
        restPeriod: '90-120s'
      }
    },
    // Weeks 8-11: Intensification. Ceiling raised 0.82 → 0.88 to close a
    // train/test mismatch: W12 tests a 5RM (~87% 1RM) and 3RM (~90%), but the
    // program never trained above 82%, so the athlete was asked to express an
    // intensity they'd never handled. Peaking near 88% makes the test a
    // demonstration of trained capacity instead of a novel stressor.
    {
      weeks: [8, 9, 10, 11],
      type: 'intensification',
      name: 'Strength Building',
      focus: 'Increasing maximal strength — heavy exposure for the W12 retest',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0,
      progression: {
        type: 'linear',
        startIntensity: 0.78,
        endIntensity: 0.88,
        weeklyIncrease: 0.025
      },
      trainingParameters: {
        repRange: [4, 6],
        setsMultiplier: 1.0,
        restPeriod: '2-3min'
      }
    }
  ]
};

// BASE TEMPLATES

// Grunt testing week split Mon/Wed/Fri so each test is a fresh
// max-effort — same rationale as Specialist. Strength tests Monday,
// bodyweight + work capacity Wednesday, run/ruck time trial Friday.

const testingStrengthDay: WorkoutTemplateDay = {
  name: 'Strength Test',
  sessionType: 'assessment',
  estimatedDuration: 65,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '5 min', format: 'prep', details: 'Bike or row — thorough before max-effort testing' },
        { name: 'Ankle Rocks', sets: 1, reps: '10/side', format: 'prep', details: 'Dorsiflexion for squat depth' },
        { name: 'Hip Flexor Opener', duration: '30s/side', format: 'prep', details: 'Open the hips before squatting + hinging' },
        { name: 'Glute Bridge', sets: 2, reps: '10', format: 'prep', details: 'Posterior-chain activation' },
        { name: 'Band Pull-Apart', sets: 2, reps: '15', format: 'prep', details: 'Scap prime before the bench + row' },
        { name: 'Warm-Up Ramp Sets', format: 'prep', details: 'Ramp each lift empty bar → 40% → 60% → 75% → 85% before your first max set. Do not test cold.' },
      ],
    },
    {
      name: 'Program Maxes',
      type: 'testing',
      exercises: [
        { name: 'Back Squat', sets: 1, reps: '5RM', intensity: 'Max', details: 'Work up to best 5-rep load. Spotter required.', sessionType: 'assessment' },
        { name: 'Trap Bar Deadlift', sets: 1, reps: '3RM', intensity: 'Max', details: 'Work up to best 3-rep load. Reset between reps.', sessionType: 'assessment' },
        { name: 'Bench Press', sets: 1, reps: '5RM', intensity: 'Max', details: 'Controlled tempo, pause briefly on chest. Spotter.', sessionType: 'assessment' },
        { name: 'Bent-Over Row', sets: 1, reps: '8RM', intensity: 'Max', details: 'Strict, no torso sway. Pendlay-style from the floor.', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Couch Stretch', duration: '60s/side', format: 'recovery', details: 'Quads + hip flexors after squatting' },
        { name: 'Dead Hang', duration: '30s', format: 'recovery', details: 'Decompress the spine after the pulls' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate' },
      ],
    },
  ],
};

const testingBodyweightDay: WorkoutTemplateDay = {
  name: 'Bodyweight + Work Capacity Test',
  sessionType: 'assessment',
  estimatedDuration: 45,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '4 min', format: 'prep', details: 'Build a light sweat before testing' },
        { name: 'Band Pull-Apart', sets: 2, reps: '15', format: 'prep', details: 'Scap + rear delt for the pull-up test' },
        { name: 'Scapular Push-Ups', sets: 2, reps: 10, format: 'prep', details: 'Prime the shoulders for the push-up test' },
      ],
    },
    {
      name: 'Bodyweight Maxes',
      type: 'testing',
      exercises: [
        { name: 'Push-Ups', sets: 1, reps: 'AMRAP', format: 'bodyweight', details: 'Max strict reps in 2 minutes', sessionType: 'assessment' },
        { name: 'Pull-Ups', sets: 1, reps: 'AMRAP', format: 'bodyweight', details: 'Strict, no kipping. Full extension at the bottom.', sessionType: 'assessment' },
        { name: 'Plank Hold', sets: 1, reps: 'Max Time', format: 'bodyweight', details: 'Stop when form breaks (hips sag).', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Work Capacity',
      type: 'testing',
      exercises: [
        { name: 'Farmer Carry', sets: 1, reps: '100m', format: 'carry', details: 'Bodyweight total load. No setdowns.', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Wall Pec Stretch', duration: '45s/side', format: 'recovery', details: 'Open the chest after the push-ups' },
        { name: 'Dead Hang', duration: '30s', format: 'recovery', details: 'Decompress + flush the grip after the pull-ups + carry' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate' },
      ],
    },
  ],
};

const testingAerobicDay: WorkoutTemplateDay = {
  name: '2-Mile Run Test',
  sessionType: 'assessment',
  estimatedDuration: 40,
  blocks: [
    {
      name: 'Pre-Run Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Easy Run', duration: '5 min', format: 'cardio', intensity: 'Easy', details: 'Brisk walk-to-jog to raise heart rate' },
        { name: 'Leg Swings', sets: 2, reps: '10/side', format: 'prep', details: 'Dynamic hips before the time trial' },
        { name: 'Glute Bridge', sets: 1, reps: '15', format: 'prep', details: 'Glute activation before running hard' },
        { name: 'Strides', sets: 4, reps: '40m', format: 'prep', details: 'Build 60% → 90% so the first mile isn\'t your warm-up' },
      ],
    },
    {
      name: 'Time Trial',
      type: 'testing',
      exercises: [
        { name: '2 Mile Run', duration: 'For Time', format: 'cardio', details: 'Record total time. Pace it — first mile should not be your fastest mile.', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Easy Run', duration: '5 min', format: 'cardio', intensity: 'Easy', details: 'Walk it out. Heart rate drops naturally.' },
        { name: 'Calf Stretch', duration: '45s/side', format: 'recovery', details: 'Straight + bent knee after the time trial' },
        { name: 'Hamstring Stretch', duration: '45s/side', format: 'recovery', details: 'Decompress after the run' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate' },
      ],
    },
  ],
};

const lowerStrengthDay: WorkoutTemplateDay = {
  name: 'Lower Body Strength',
  sessionType: 'pt',
  estimatedDuration: 75,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Bike or row — raise core temp' },
        { name: 'Ankle Rocks', sets: 1, reps: '10/side', format: 'prep', details: 'Dorsiflexion for squat depth' },
        { name: 'Hip Flexor Opener', duration: '30s/side', format: 'prep', details: 'Open the hips before squatting + hinging' },
        { name: 'Glute Bridge', sets: 2, reps: '10', format: 'prep', details: 'Posterior-chain activation before the deadlift' },
        { name: 'Hip Hinge Drill', sets: 1, reps: 10, format: 'prep', details: 'Groove the hinge before loading the trap bar' },
      ],
    },
    {
      name: 'Lower Body',
      type: 'strength',
      exercises: [
        { name: 'Back Squat', sets: 4, reps: 8, intensity: 'progressive', rest: '2-3 min', details: 'Focus on depth — brace hard out of the hole' },
        { name: 'Trap Bar Deadlift', sets: 4, reps: 6, intensity: 'progressive', rest: '2-3 min', details: 'Neutral spine, push the floor away' },
      ],
    },
    {
      // Posterior chain + unilateral. The RDL is new: the program rucks 40-60
      // min a week and runs a 2-mile test but had NO direct hamstring work —
      // and the hamstring is a primary strain site under exactly that load.
      name: 'Posterior + Unilateral',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Romanian Deadlift', sets: 3, reps: 8, intensity: 'progressive', rest: '90s', details: 'Hinge, feel the hamstring stretch — control the lowering' },
        { name: 'Walking Lunge', sets: 3, reps: 10, intensity: 'progressive', rest: '90s', details: 'Per leg — add load as you progress past bodyweight' },
      ],
    },
    {
      // Soleus work is deliberately seated: it's the primary calf muscle in
      // sustained loaded walking/running and the tissue implicated in shin
      // splints and achilles issues. The program's ruck volume earned this.
      name: 'Durability + Core',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Seated Calf Raise', sets: 3, reps: 12, intensity: 'progressive', rest: '60s', details: 'Soleus — protects the achilles + shins against the ruck volume' },
        { name: 'Dead Bug', sets: 3, reps: '10/side', intensity: 'progressive', rest: '60s', details: 'Anti-extension — ribs down, low back flat to the floor' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Couch Stretch', duration: '60s/side', format: 'recovery', details: 'Quads + hip flexors after squatting' },
        { name: 'Hamstring Stretch', duration: '45s/side', format: 'recovery', details: 'After the hinging volume' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate after the heavy work' },
      ],
    },
  ],
};

const upperStrengthDay: WorkoutTemplateDay = {
  name: 'Upper Body Strength',
  sessionType: 'pt',
  estimatedDuration: 70,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '2 min', format: 'prep', details: 'Rower — warm the upper back' },
        { name: 'Band Pull-Apart', sets: 2, reps: '15', format: 'prep', details: 'Scap retraction + rear delt before pressing and pulling' },
        { name: 'Band External Rotation', sets: 2, reps: '12/side', format: 'prep', details: 'Rotator-cuff prime before the bench + press' },
        { name: 'Scapular Push-Ups', sets: 2, reps: 10, format: 'prep', details: 'Serratus + scap control for pressing' },
        { name: 'T-Spine Extension', sets: 1, reps: '8', format: 'prep', details: 'Thoracic mobility for the overhead press' },
      ],
    },
    {
      name: 'Upper Body',
      type: 'strength',
      exercises: [
        { name: 'Bench Press', sets: 4, reps: 8, intensity: 'progressive', rest: '2-3 min', details: 'Controlled descent, full lockout' },
        // Pull-Up added: the W1/W12 battery TESTS max pull-ups, but the training
        // weeks contained no vertical pulling at all — benchmarking a quality the
        // program never trained. Now it's trained twice a week (here + the
        // conditioning circuit), which also lifts pulling frequency to 2×.
        { name: 'Pull-Up', sets: 4, reps: 6, intensity: 'progressive', rest: '2 min', details: 'Vertical pull — scale to assisted/inverted row if you can\'t hit 6; add load when you can' },
        { name: 'Bent-Over Row', sets: 3, reps: 10, intensity: 'progressive', rest: '90s', details: 'Controlled, no torso swing' },
        { name: 'Overhead Press', sets: 3, reps: 8, intensity: 'progressive', rest: '90s', details: 'Strict, full range of motion' },
      ],
    },
    {
      name: 'Core',
      type: 'accessory',
      exercises: [
        { name: 'Pallof Press', sets: 3, reps: '12/side', intensity: 'progressive', rest: '60s', details: 'Anti-rotation — resist the twist, slow and strict' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Wall Pec Stretch', duration: '45s/side', format: 'recovery', details: 'Open the chest after pressing' },
        { name: 'Dead Hang', duration: '30s', format: 'recovery', details: 'Decompress + flush the lats after the pulls' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate' },
      ],
    },
  ],
};

// Conditioning Day = anaerobic + work-capacity focus.
// The long ruck moved to Endurance Day so each session targets ONE energy
// system instead of jamming three into 45 min. Old Grunt tried to do
// long aerobic + intervals + carries in one slot, which is recovery debt
// for no clear adaptation.
const conditioningDay: WorkoutTemplateDay = {
  name: 'Intervals + Work Capacity',
  sessionType: 'pt',
  estimatedDuration: 50,
  blocks: [
    {
      // Was missing entirely — the session opened with 8 × 200m at RPE 8 from
      // cold. Hard running on unprepped tissue is the classic hamstring strain.
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Easy jog — build a light sweat' },
        { name: 'Leg Swings', sets: 2, reps: '10/side', format: 'prep', details: 'Front-back + lateral — dynamic hips' },
        { name: 'Glute Bridge', sets: 1, reps: '15', format: 'prep', details: 'Glute activation before sprinting' },
        { name: 'Strides', sets: 4, reps: '40m', format: 'prep', details: 'Build 60% → 90%. Ramping the tissue before hard 200s is how you avoid a hamstring tear.' },
      ],
    },
    {
      name: 'Anaerobic Intervals',
      type: 'conditioning',
      format: 'intervals',
      exercises: [
        { name: 'Interval Run', sets: 8, reps: '200m', format: 'intervals', details: 'Hard effort 200m, walk 200m recovery. RPE 8 on the work intervals.' },
      ],
    },
    {
      name: 'Work Capacity Circuit',
      type: 'circuit',
      format: 'circuit',
      exercises: [
        { name: 'Farmer Carry', sets: 4, reps: '100m', format: 'circuit', details: 'Bodyweight total load, steady pace, no setdowns' },
        { name: 'Burpees', sets: 4, reps: 10, format: 'circuit', details: 'Controlled tempo, full hip extension' },
        { name: 'Push-Up', sets: 4, reps: 'AMRAP', format: 'circuit', details: 'Second weekly pressing exposure — chest to deck, strict' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Hamstring Stretch', duration: '45s/side', format: 'recovery', details: 'Hamstrings take the brunt of the intervals' },
        { name: 'Calf Stretch', duration: '45s/side', format: 'recovery', details: 'Straight + bent knee' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate the effort' },
      ],
    },
  ],
};

// Endurance Day = aerobic base — moved the long ruck here so the conditioning
// stimulus is unambiguous. Long efforts on Day 4 build the aerobic engine
// that Specialist will demand.
const enduranceDay: WorkoutTemplateDay = {
  name: 'Long Aerobic + Mobility',
  sessionType: 'pt',
  estimatedDuration: 60,
  blocks: [
    {
      name: 'Aerobic Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Brisk unloaded walk before loading up' },
        { name: 'Ankle Rocks', sets: 1, reps: '10/side', format: 'prep', details: 'Dorsiflexion for a loaded gait' },
        { name: 'Glute Bridge', sets: 2, reps: '10', format: 'prep', details: 'Carry the load with the posterior chain, not the low back' },
      ],
    },
    {
      // ALTERNATE ruck and run week to week. The program tests a 2-MILE RUN at
      // W1/W12 but previously built its aerobic base entirely by RUCKING —
      // different gait, different economy. Alternating keeps the ruck capacity
      // while actually training the modality the benchmark measures.
      name: 'Long Aerobic',
      type: 'conditioning',
      exercises: [
        { name: 'Ruck March', duration: '40-60 min', format: 'ruck', details: 'ODD weeks — steady conversational pace, 25-35 lb. The aerobic engine everything rides on.' },
        { name: 'Easy Run', duration: '30-40 min', format: 'cardio', intensity: 'Z2', details: 'EVEN weeks — conversational Zone 2 run. Builds the running economy the 2-mile test actually measures.' },
      ],
    },
    {
      name: 'Durability',
      type: 'accessory',
      exercises: [
        { name: 'Copenhagen Plank', sets: 3, reps: 'Max Hold', format: 'bodyweight', details: 'Adductor stability — groin strains are a rucking classic' },
        { name: 'Side Plank', sets: 3, reps: '30s each', format: 'bodyweight', details: 'Anti-lateral-flexion — resists the pack pulling you sideways' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Calf Stretch', duration: '45s/side', format: 'recovery', details: 'Straight + bent knee — the long effort loads the calves hardest' },
        { name: 'Couch Stretch', duration: '60s/side', format: 'recovery', details: 'Hip flexors + quads after the miles' },
        { name: 'Lower Back Stretch', duration: '1 min', format: 'recovery', details: 'Child\'s pose / cat-cow — decompress the spine after the pack' },
      ],
    },
  ],
};

const restDay: WorkoutTemplateDay = {
  name: 'Rest Day',
  sessionType: 'pt',
  estimatedDuration: 0,
  blocks: [],
};

export const gruntProgram12Week: WorkoutProgram = {
  metadata,
  periodization,
  templates: {
    // Testing weeks (W1, W12) — split Mon/Wed/Fri so each test is fresh
    // and benchmark numbers are clean. Strength, bodyweight+capacity, run.
    'testing-week-day1': testingStrengthDay,
    'testing-week-day2': restDay,
    'testing-week-day3': testingBodyweightDay,
    'testing-week-day4': restDay,
    'testing-week-day5': testingAerobicDay,
    'testing-week-day6': restDay,
    'testing-week-day7': restDay,

    // Training cycle (W2-11)
    'training-day1': lowerStrengthDay,
    'training-day2': upperStrengthDay,
    'training-day3': conditioningDay,
    'training-day4': enduranceDay,
    'training-day5': restDay,
    'training-day6': restDay,
    'training-day7': restDay,
  },
  workouts: [],
};

